import { FormProps } from '@alphabait/ui/form'
import { END_USER_THEME_COLOR_ROLES } from '../../../../server/src/customer/end-user-theme.schema'
import { useController, Control } from 'react-hook-form'
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { WYSIWYGInput } from './custom-types'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useCustomerClient } from '@/lib/api/ts-rest-client'
import { cn } from '@alphabait/ui/lib/utils'

// Override Bold, Italic, and Underline to use inline styles instead of semantic tags
const Inline = Quill.import('blots/inline')

class BoldBlot extends Inline {
  static blotName = 'bold'
  static tagName = 'span'
  static className = 'ql-bold'

  static create() {
    const node = super.create()
    node.style.fontWeight = 'bold'
    return node
  }

  static formats(node: HTMLElement) {
    return node.style.fontWeight === 'bold'
  }
}

class ItalicBlot extends Inline {
  static blotName = 'italic'
  static tagName = 'span'
  static className = 'ql-italic'

  static create() {
    const node = super.create()
    node.style.fontStyle = 'italic'
    return node
  }

  static formats(node: HTMLElement) {
    return node.style.fontStyle === 'italic'
  }
}

class UnderlineBlot extends Inline {
  static blotName = 'underline'
  static tagName = 'span'
  static className = 'ql-underline'

  static create() {
    const node = super.create()
    node.style.textDecoration = 'underline'
    return node
  }

  static formats(node: HTMLElement) {
    return node.style.textDecoration === 'underline'
  }
}

// Use style-based font attributor for inline font-family styles
// This ensures fonts work in email previews and actual emails
const FontStyle = Quill.import('attributors/style/font') as any
FontStyle.whitelist = null // allow any font, we control options via toolbar

Quill.register(BoldBlot, true)
Quill.register(ItalicBlot, true)
Quill.register(UnderlineBlot, true)
Quill.register(FontStyle, true)

// Legacy mode props (used with AlphaForm)
type LegacyWYSIWYGProps = {
  field: WYSIWYGInput
  formProps: FormProps
  brand?: string | null
  customerId?: string
}

// Direct mode props (used with plain react-hook-form)
type DirectWYSIWYGProps = {
  name: string
  control: Control<any>
  brand?: string | null
  className?: string
  maxChars?: number
  customerId?: string
  disableFontOptions?: boolean
  // The customer's end-user theme row (admin campaign payload); its colours join the swatches.
  endUserTheme?: Record<string, unknown> | null
  // Extra font names for the font picker. Only the postcard editor passes the theme
  // font: the PDF embeds it, emails cannot.
  extraFonts?: string[]
}

// Discriminated union - component accepts either legacy or direct props
type WYSIWYGProps = LegacyWYSIWYGProps | DirectWYSIWYGProps

function isLegacyWYSIWYGProps(
  props: WYSIWYGProps,
): props is LegacyWYSIWYGProps {
  return 'field' in props && 'formProps' in props
}

// Helper to normalize font names for CSS classes and data attributes
const normalizeFontName = (fontName: string): string => {
  return fontName.toLowerCase().replace(/\s+/g, '-')
}

// Map of normalized font names to their CSS font-family values
const getFontFamily = (fontName: string): string => {
  const normalized = normalizeFontName(fontName)
  const fontMap: Record<string, string> = {
    poppins: "'Poppins', sans-serif",
    inter: "'Inter', sans-serif",
    courier: "'Courier New', monospace",
  }
  return fontMap[normalized] || `'${fontName}', sans-serif`
}

const WYSIWYG = (props: WYSIWYGProps) => {
  // Extract the required values from either legacy or direct props
  const fieldName = isLegacyWYSIWYGProps(props) ? props.field.name : props.name
  const control = isLegacyWYSIWYGProps(props)
    ? props.formProps.control
    : props.control
  const customerId = props.customerId
  const className = isLegacyWYSIWYGProps(props)
    ? props.field.uiOptions?.className
    : props.className
  const maxChars = isLegacyWYSIWYGProps(props)
    ? props.field.maxChars
    : props.maxChars
  const disableFontOptions = isLegacyWYSIWYGProps(props)
    ? false
    : props.disableFontOptions ?? false
  const formValues = isLegacyWYSIWYGProps(props)
    ? props.formProps.formValues
    : undefined
  const endUserTheme = isLegacyWYSIWYGProps(props) ? null : props.endUserTheme
  const extraFonts = isLegacyWYSIWYGProps(props) ? [] : props.extraFonts ?? []

  const { t } = useTranslation()
  const customerClient = useCustomerClient()

  // Fetch customer settings (fonts, colors, etc.)
  const {
    data: customerSettings,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['customerSettings', customerId],
    queryFn: async () => {
      const response = await customerClient.settings.query({
        params: { customerId },
      })
      return response.status === 200 ? response.body : null
    },
  })

  if (queryError) {
    console.error('Error fetching customer settings:', queryError)
  }

  // Get font list - plain names used by the style-based font attributor
  const availableFonts = useMemo(() => {
    const base = customerSettings?.availableFontNames || ['Poppins', 'Inter']
    return Array.from(new Set([...base, ...extraFonts]))
  }, [customerSettings, extraFonts])

  // Generate dynamic CSS styles for the font picker dropdown labels
  const fontStyles = useMemo(() => {
    return availableFonts
      .map((fontName: string) => {
        const family = getFontFamily(fontName)
        return `
        .ql-snow .ql-picker.ql-font .ql-picker-label[data-value="${fontName}"]::before,
        .ql-snow .ql-picker.ql-font .ql-picker-item[data-value="${fontName}"]::before {
          content: '${fontName}';
          font-family: ${family};
        }
      `
      })
      .join('\n')
  }, [availableFonts])

  const modules = useMemo(() => {
    // Build color palette from customer settings
    // Start with standard colors
    const standardColors = [
      '#000000', // Black
    ]

    // Add customer's custom colors from settings
    const customerColors = customerSettings?.colors
      ? Object.values(customerSettings.colors)
      : []

    const themeColors = END_USER_THEME_COLOR_ROLES.map(
      (role) => endUserTheme?.[role],
    ).filter((value): value is string => typeof value === 'string')

    const colors = Array.from(
      new Set([...standardColors, ...customerColors, ...themeColors]),
    )

    const toolbarOptions = [
      ['bold', 'italic', 'underline'],
      ...(disableFontOptions
        ? []
        : [
            [{ font: [false, ...availableFonts] }],
            [{ size: ['small', false, 'large', 'huge'] }],
          ]),
      [{ color: colors }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['clean'], // Clear formatting button
    ]

    return {
      toolbar: toolbarOptions,
    }
  }, [availableFonts, customerSettings, disableFontOptions, endUserTheme])

  const stripHtmlTags = (html: string) => {
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent || div.innerText || ''
  }

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    control,
    name: fieldName,
  })

  const quillRef = useRef<ReactQuill>(null)
  const plainTextRef = useRef('')

  const sanitizeContent = useCallback((html: string) => {
    return html.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ')
  }, [])

  // Sync editor content when the form value changes programmatically
  // (e.g. RHF reset/setValue from a parent). ReactQuill only reads
  // `defaultValue` at mount, so without this, programmatic updates are lost.
  useEffect(() => {
    const editor = quillRef.current?.getEditor()
    if (!editor) return

    const normalize = (s: string) =>
      s === '<p><br></p>' ? '' : sanitizeContent(s)

    if (normalize(editor.root.innerHTML) === normalize(value || '')) return

    editor.clipboard.dangerouslyPasteHTML(value || '', 'silent')
  }, [value, sanitizeContent])

  const handleChange = useCallback(
    (content: string, _delta: any, source: string) => {
      // Only process user-initiated changes
      if (source !== 'user') {
        return
      }

      const emptyContentRegex = /^<p><br><\/p>$/
      const currentContent = emptyContentRegex.test(content) ? '' : content

      const plainText = stripHtmlTags(currentContent)
      plainTextRef.current = plainText

      if (maxChars && plainText.length > maxChars) {
        let currentPlainTextLength = 0
        const parser = new DOMParser()
        const doc = parser.parseFromString(currentContent, 'text/html')
        const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)

        while (walker.nextNode()) {
          const node = walker.currentNode
          if (node.textContent) {
            const remainingChars = maxChars - currentPlainTextLength
            if (node.textContent.length <= remainingChars) {
              currentPlainTextLength += node.textContent.length
            } else {
              node.textContent = node.textContent.substring(0, remainingChars)
              break
            }
          }
        }

        const serializer = new XMLSerializer()
        const truncatedHtmlContent = serializer.serializeToString(doc.body)
        onChange(sanitizeContent(truncatedHtmlContent))
      } else {
        onChange(sanitizeContent(currentContent))
      }
    },
    [maxChars, onChange, sanitizeContent],
  )

  const plainTextContent = formValues
    ? stripHtmlTags(formValues[fieldName])
    : stripHtmlTags(value || '')

  if (loading) {
    return <div>Loading editor...</div>
  }

  return (
    <>
      <style>{`
        ${fontStyles}
        .ql-size-small {
          font-size: 0.75em !important;
        }
        .ql-size-large {
          font-size: 1.5em !important;
        }
        .ql-size-huge {
          font-size: 2.5em !important;
        }
        .ql-bold {
          font-weight: bold !important;
        }
        .ql-italic {
          font-style: italic !important;
        }
        .ql-underline {
          text-decoration: underline !important;
        }
        .wysiwyg-wrapper .ql-container {
          overflow-y: auto;
          border: none;
        }
        .wysiwyg-wrapper.h-40 .ql-container {
          max-height: 7rem;
        }
        .wysiwyg-wrapper.h-80 .ql-container {
          max-height: 17rem;
        }
      `}</style>
      {/* reason: Quill builds its toolbar once at mount; fonts that arrive later (customer
          settings, the theme font) need a fresh editor to reach the dropdown. */}
      <ReactQuill
        key={availableFonts.join('|')}
        ref={quillRef}
        defaultValue={value || ''}
        onChange={handleChange}
        modules={modules}
        theme="snow"
        className={cn(
          'wysiwyg-wrapper',
          className,
          'border border-base-input bg-background-light rounded-md w-full',
        )}
      />

      {maxChars && (
        <p className="text-xs text-right mt-2">
          {maxChars - plainTextContent.length}{' '}
          {maxChars - plainTextContent.length === 1
            ? 'character'
            : 'characters'}{' '}
          left
        </p>
      )}
      {error && (
        <p className="text-primary text-sm mt-1">
          {t(`validation_messages.${error.message}`, {
            defaultValue: error.message,
          })}
        </p>
      )}
    </>
  )
}

export default WYSIWYG
